import {captureException} from '@sentry/browser';

import {assert} from 'Asserts';
import {warn} from 'util/Logging';

const DATA_CHANNEL_NAME = 'oai-events';
export const DATA_CHANNEL_OPEN_TIMEOUT_MS = 10_000;
export const DATA_EVENT_WAIT_TIMEOUT_MS = 10_000;
const MICROPHONE_LEVEL_SAMPLE_INTERVAL_MS = 50;
const MICROPHONE_LEVEL_NOISE_FLOOR = 0.01;
const MICROPHONE_LEVEL_FULL_SCALE_RMS = 0.12;

class MicrophoneLevelMonitor {
  private readonly analyser: AnalyserNode;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly samples: Float32Array<ArrayBuffer>;
  private timerId: number | null = null;
  private smoothedLevel = 0;

  constructor(
    microphoneStream: MediaStream,
    private readonly audioContext: AudioContext,
    private readonly levelListener: (level: number) => void
  ) {
    this.source = this.audioContext.createMediaStreamSource(microphoneStream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 512;
    this.samples = new Float32Array(this.analyser.fftSize);
    this.source.connect(this.analyser);
    this.sample();
  }

  private sample = (): void => {
    this.analyser.getFloatTimeDomainData(this.samples);
    const sumOfSquares = this.samples.reduce((sum, value) => sum + value * value, 0);
    const rms = Math.sqrt(sumOfSquares / this.samples.length);
    const normalizedLevel = Math.max(
      0,
      Math.min(
        1,
        (rms - MICROPHONE_LEVEL_NOISE_FLOOR) /
          (MICROPHONE_LEVEL_FULL_SCALE_RMS - MICROPHONE_LEVEL_NOISE_FLOOR)
      )
    );
    // React quickly to speech while letting the cloud settle instead of flickering between samples.
    this.smoothedLevel = Math.max(normalizedLevel, this.smoothedLevel * 0.72);
    this.levelListener(this.smoothedLevel);
    this.timerId = window.setTimeout(this.sample, MICROPHONE_LEVEL_SAMPLE_INTERVAL_MS);
  };

  async close(): Promise<void> {
    if (this.timerId != null) {
      window.clearTimeout(this.timerId);
    }
    this.levelListener(0);
    this.source.disconnect();
    await this.audioContext.close();
  }
}

interface DataEventWaiter {
  handleEvent: (event: unknown) => void;
  reject: (error: Error) => void;
}

/**
 * WebRTC session manager for realtime voice communication with an LLM.
 *
 * The initial target is the OpenAI Realtime API, see docs and code samples here:
 * https://platform.openai.com/docs/guides/realtime-webrtc
 *
 * This class should encapsulate the DOM-specific components, including audio playback and microphone
 * input. It is mocked for testing, so also should isolate anything DOM-related and avoid other business
 * logic.
 *
 * Usage:
 * const session = await WebRTCSession.create();
 * const sdpOffer = await session.createSDPOffer();
 * await session.startSessionWithSDPAnswer([get SDP answer based on offer]);
 * ...
 * session.close();
 */
export default class WebRTCSession {
  private wakeLock: WakeLockSentinel | null = null;
  private readonly rejectDataChannelOpenWaiters = new Set<(error: Error) => void>();
  private readonly dataEventWaiters = new Set<DataEventWaiter>();

  private constructor(
    private readonly peerConnection: RTCPeerConnection,
    private readonly dataChannel: RTCDataChannel,
    private readonly microphoneStream: MediaStream,
    private readonly audioElement: HTMLAudioElement,
    private readonly microphoneLevelMonitor: MicrophoneLevelMonitor | null,
    private readonly dataEventListener: (event: unknown) => Promise<void> | void
  ) {
    this.dataChannel.addEventListener('message', (event) => {
      this.handleDataChannelMessage(event);
    });
    this.dataChannel.addEventListener('close', () => {
      this.rejectDataEventWaiters(new Error('Voice data channel closed while awaiting a server event.'));
    });
    this.dataChannel.addEventListener('error', () => {
      this.rejectDataEventWaiters(new Error('Voice data channel failed while awaiting a server event.'));
    });
  }

  /**
   * Create and initialize a new WebRTC session.
   *
   * Accepts a data event callback that takes the parsed JSON data of each event.
   */
  static async create(
    dataEventListener: (event: unknown) => Promise<void> | void,
    connectionStateListener: (state: RTCPeerConnectionState) => void = () => {},
    microphoneLevelListener?: (level: number) => void
  ): Promise<WebRTCSession> {
    // Resume while the start-button user activation is still synchronous. Waiting until after
    // getUserMedia resolves can leave the analyser suspended behind browser autoplay policy.
    const microphoneLevelAudioContext = microphoneLevelListener == null ? null : new window.AudioContext();
    microphoneLevelAudioContext?.resume().catch(captureException);
    const peerConnection = new window.RTCPeerConnection();
    peerConnection.addEventListener('connectionstatechange', () => {
      connectionStateListener(peerConnection.connectionState);
    });
    const dataChannel = peerConnection.createDataChannel(DATA_CHANNEL_NAME);

    const audioElement = document.createElement('audio');
    audioElement.autoplay = true;
    peerConnection.ontrack = (event) => {
      audioElement.srcObject = event.streams[0];
    };

    let microphoneStream: MediaStream;
    try {
      microphoneStream = await window.navigator.mediaDevices.getUserMedia({audio: true});
    } catch (error) {
      await microphoneLevelAudioContext?.close();
      throw error;
    }
    for (const track of microphoneStream.getTracks()) {
      // Media and data use separate WebRTC paths. Keep the microphone hard-muted before it is
      // attached so no live turn can reach the server while session context is being initialized.
      track.enabled = false;
      peerConnection.addTrack(track);
    }
    const microphoneLevelMonitor =
      microphoneLevelListener == null || microphoneLevelAudioContext == null
        ? null
        : new MicrophoneLevelMonitor(microphoneStream, microphoneLevelAudioContext, microphoneLevelListener);

    return new WebRTCSession(
      peerConnection,
      dataChannel,
      microphoneStream,
      audioElement,
      microphoneLevelMonitor,
      dataEventListener
    );
  }

  private handleDataChannelMessage(event: MessageEvent): void {
    const data = WebRTCSession.parseDataEvent(event);
    if (data == null) {
      return;
    }
    for (const waiter of this.dataEventWaiters) {
      waiter.handleEvent(data);
    }
    Promise.resolve()
      .then(() => this.dataEventListener(data))
      .catch(captureException);
  }

  /**
   * Parse the JSON payload out of a raw data event.
   */
  static parseDataEvent(event: unknown): unknown {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return JSON.parse((event as any).data);
    } catch (e) {
      if (!(e instanceof SyntaxError)) {
        throw e;
      }
      warn('[WebRTCSession] Failed to parse data event.');
      return null;
    }
  }

  /**
   * Send the given event object (must be JSON-serializable) via the data channel.
   */
  sendDataEvent(event: unknown): void {
    this.dataChannel.send(JSON.stringify(event));
  }

  /**
   * Resolve when a future data event satisfies `isComplete`, or reject on a correlated server
   * error, data-channel failure, session close, or timeout.
   */
  waitForDataEvent(
    isComplete: (event: unknown) => boolean,
    getError: (event: unknown) => Error | null,
    abortSignal?: AbortSignal
  ): Promise<void> {
    if (this.dataChannel.readyState !== 'open') {
      return Promise.reject(new Error('Voice data channel is not open while awaiting a server event.'));
    }
    if (abortSignal?.aborted) {
      return Promise.reject(
        abortSignal.reason instanceof Error
          ? abortSignal.reason
          : new Error('Cancelled waiting for a Voice server event.')
      );
    }

    return new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout>;
      const cleanup = (): void => {
        clearTimeout(timeoutId);
        this.dataEventWaiters.delete(waiter);
        abortSignal?.removeEventListener('abort', handleAbort);
      };
      const rejectWaiter = (error: Error): void => {
        cleanup();
        reject(error);
      };
      const waiter: DataEventWaiter = {
        handleEvent: (event) => {
          Promise.resolve()
            .then(() => {
              const error = getError(event);
              if (error != null) {
                rejectWaiter(error);
              } else if (isComplete(event)) {
                cleanup();
                resolve();
              }
            })
            .catch((error: unknown) => {
              rejectWaiter(error instanceof Error ? error : new Error(String(error)));
            });
        },
        reject: rejectWaiter,
      };
      const handleAbort = (): void => {
        rejectWaiter(
          abortSignal?.reason instanceof Error
            ? abortSignal.reason
            : new Error('Cancelled waiting for a Voice server event.')
        );
      };

      this.dataEventWaiters.add(waiter);
      abortSignal?.addEventListener('abort', handleAbort, {once: true});
      timeoutId = setTimeout(() => {
        rejectWaiter(new Error('Timed out waiting for the Voice server to acknowledge session context.'));
      }, DATA_EVENT_WAIT_TIMEOUT_MS);
    });
  }

  /** Enable live microphone samples after session context initialization is acknowledged. */
  enableMicrophone(): void {
    for (const track of this.microphoneStream.getTracks()) {
      track.enabled = true;
    }
  }

  private rejectDataEventWaiters(error: Error): void {
    for (const waiter of this.dataEventWaiters) {
      waiter.reject(error);
    }
  }

  /**
   * Resolve once the data channel is open and safe to send on.
   *
   * The channel opens asynchronously after the SDP answer is applied; sending before it is open
   * throws InvalidStateError. Callers that post events right after connecting (e.g. conversation-
   * history replay, BNCH-167097) must await this first. Resolves immediately if already open,
   * otherwise on a one-shot 'open' event. Rejects if the channel errors, closes, the session is
   * torn down, or the bounded wait expires.
   */
  waitForDataChannelOpen(): Promise<void> {
    if (this.dataChannel.readyState === 'open') {
      return Promise.resolve();
    }
    if (this.dataChannel.readyState === 'closing' || this.dataChannel.readyState === 'closed') {
      return Promise.reject(new Error('Voice data channel closed before it opened.'));
    }

    return new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout>;
      // Cleanup only unregisters this waiter; the settlement helpers own resolving or rejecting it.
      const cleanup = (): void => {
        clearTimeout(timeoutId);
        this.dataChannel.removeEventListener('open', handleOpen);
        this.dataChannel.removeEventListener('close', handleClose);
        this.dataChannel.removeEventListener('error', handleError);
        this.rejectDataChannelOpenWaiters.delete(rejectWaiter);
      };
      const resolveWaiter = (): void => {
        cleanup();
        resolve();
      };
      const rejectWaiter = (error: Error): void => {
        cleanup();
        reject(error);
      };
      const handleOpen = (): void => {
        resolveWaiter();
      };
      const handleClose = (): void => {
        rejectWaiter(new Error('Voice data channel closed before it opened.'));
      };
      const handleError = (): void => {
        rejectWaiter(new Error('Voice data channel failed before it opened.'));
      };

      this.rejectDataChannelOpenWaiters.add(rejectWaiter);
      this.dataChannel.addEventListener('open', handleOpen);
      this.dataChannel.addEventListener('close', handleClose);
      this.dataChannel.addEventListener('error', handleError);
      timeoutId = setTimeout(() => {
        rejectWaiter(new Error('Timed out waiting for the Voice data channel to open.'));
      }, DATA_CHANNEL_OPEN_TIMEOUT_MS);
    });
  }

  /**
   * Create an SDP offer for this session.
   */
  async createSDPOffer(): Promise<string> {
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    const sdp = offer.sdp;
    assert(sdp != null, 'Unexpectedly received null SDP');
    return sdp;
  }

  /**
   * Start the session based on the SDP answer received from the server.
   */
  async startSessionWithSDPAnswer(sdpAnswer: string): Promise<void> {
    await this.peerConnection.setRemoteDescription({
      type: 'answer',
      sdp: sdpAnswer,
    });
    await this.acquireWakeLock();
  }

  /**
   * Acquire a wake lock to keep the screen awake during the session.
   */
  private async acquireWakeLock(): Promise<void> {
    try {
      this.wakeLock = await window.navigator.wakeLock.request('screen');
    } catch (err) {
      if (err instanceof DOMException) {
        // The wake lock may be refused, e.g. on low battery. Ignore the error in that case.
        warn(`Failed to acquire wake lock: ${err.message}`);
      } else {
        throw err;
      }
    }
  }

  /**
   * Close the session and clean up all resources.
   */
  async close(): Promise<void> {
    const closeError = new Error('Voice session closed before its data channel opened.');
    for (const rejectWaiter of this.rejectDataChannelOpenWaiters) {
      rejectWaiter(closeError);
    }
    this.rejectDataEventWaiters(new Error('Voice session closed while awaiting a server event.'));
    this.peerConnection.close();
    this.dataChannel.close();
    for (const track of this.microphoneStream.getTracks()) {
      track.stop();
    }
    await this.microphoneLevelMonitor?.close();
    this.audioElement.pause();
    await this.wakeLock?.release();
  }
}
